// src/components/DepositInfoSection.jsx
import React, { useState, useEffect } from "react";
import styles from "./DepositInfoSection.module.css";
import api from "../api";

export default function DepositInfoSection({
  form,
  setForm,
  selectedBusiness,
  bankAccounts,
  accountSearch,
  setAccountSearch,
  suggestions,
  setSuggestions,
  loadedFromDraft,
  handleSubmit,
  depositDateRef,
  setParentOriginalDescription
}) {
  // 상단에 원본 적요 보관용 상태 추가
  const [originalDescription, setOriginalDescription] = useState("");

  // 거래처 자동완성 상태
  const [vendors, setVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [showVendorList, setShowVendorList] = useState(false);

  // ✅ 입금통장 자동완성 상태
  const [bankInput, setBankInput] = useState("");
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [bankHighlightIndex, setBankHighlightIndex] = useState(-1);
  const [showBankList, setShowBankList] = useState(false);

  // ✅ 금액 관련 상태 (세 번째 줄)
  // 공급가액 / 부가세 / 수수료 / 합계 상태
  const [supplyValue, setSupplyValue] = useState("");
  const [vatValue, setVatValue] = useState("");
  const [feeValue, setFeeValue] = useState("");
  const [totalValue, setTotalValue] = useState("");

  // ⭐ 공급가액 / 부가세 / 수수료 / 합계 → form과 자동 동기화
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      supplyValue: supplyValue,
      vatValue: vatValue,
      feeValue: feeValue,
      totalValue: totalValue,
    }));
  }, [supplyValue, vatValue, feeValue, totalValue, setForm]);
  // 거래처 목록 불러오기
  useEffect(() => {
    async function fetchVendors() {
      try {
        const res = await api.get("/vendors/");
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setVendors(data);
      } catch (err) {
        console.error("❌ 거래처 목록 불러오기 실패:", err);
      }
    }
    fetchVendors();
  }, []);

  // ⭐ 기안자료 불러올 때 거래처 자동 매칭
  useEffect(() => {
    if (form.customer_name && vendors.length > 0) {
      const matchedVendor = vendors.find(
        (v) => v.name === form.customer_name || 
               v.name.includes(form.customer_name) ||
               form.customer_name.includes(v.name)
      );
      
      if (matchedVendor) {
        console.debug("[거래처] 자동 매칭:", matchedVendor.name);
        setVendorInput(matchedVendor.name);
        setBusinessNumber(matchedVendor.business_number || "");
        setForm((prev) => ({
          ...prev,
          vendor_id: matchedVendor.id,
          vendor_name: matchedVendor.name,
          business_number: matchedVendor.business_number || "",
        }));
      } else {
        // 매칭되는 거래처가 없으면 입력된 이름만 표시
        console.debug("[거래처] 매칭 실패, 입력값만 표시:", form.customer_name);
        setVendorInput(form.customer_name);
        setBusinessNumber("");
      }
    }
  }, [form.customer_name, vendors]); // ⭐ customer_name과 vendors 변경 시 실행

  // ⭐⭐ 기안자료에서 적요에 "청구서"가 포함된 경우 "입금"으로 치환
  useEffect(() => {
    if (loadedFromDraft && form.description && form.description.includes("청구서")) {
      // 원본 저장 (상단 표시용)
      setOriginalDescription(form.description);  // ✅ 로컬 상태 setter

      // 부모 컴포넌트로도 전달
      if (setParentOriginalDescription) {  // ✅ 이름 충돌 해결
        setParentOriginalDescription(form.description);
      }
      
      const newDescription = form.description.replace(/청구서/g, "입금");
      
      console.debug("[적요] 청구서 → 입금 자동 변경:", newDescription);
      
      // 타이밍을 약간 늦춰서 기안자료가 완전히 로드된 후 변경
      setTimeout(() => {
        setForm((prev) => ({
          ...prev,
          description: newDescription
        }));
      }, 100);
    }
  }, [loadedFromDraft]);

  // ✅ 거래처 자동완성 필터링
  const handleChangeVendor = (e) => {
    const value = e.target.value;
    setVendorInput(value);
    setForm(prev => ({ ...prev, vendor_name: value })); // ⭐ 추가
    if (!value.trim()) {
      setFilteredVendors([]);
      setShowVendorList(false);
      setBusinessNumber("");
      return;
    }
    const filtered = vendors.filter((v) =>
      v.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredVendors(filtered);
    setShowVendorList(true);
    setHighlightIndex(-1);
  };

  // ✅ 거래처 선택
  const handlePickVendor = (vendor) => {
    setVendorInput(vendor.name);
    setBusinessNumber(vendor.business_number || "");
    setForm((prev) => ({
      ...prev,
      vendor_id: vendor.id,
      vendor_name: vendor.name,
      business_number: vendor.business_number || "",
    }));
    setShowVendorList(false);
  };

  // ✅ 방향키로 거래처 선택
  const handleVendorKeyDown = (e) => {
    if (!showVendorList || filteredVendors.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredVendors.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredVendors.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredVendors[highlightIndex]) {
        handlePickVendor(filteredVendors[highlightIndex]);
      }
    }
  };

  // ✅ 숫자 포맷
  const formatNumber = (value) =>
    value.replace(/[^0-9]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const toNumber = (str) => Number(str?.replace(/,/g, "")) || 0;

  const handleAmountChange = (e, setter) => {
    setter(formatNumber(e.target.value));
  };

  // ✅ 합계 자동 계산
  useEffect(() => {
    const sum = toNumber(supplyValue) + toNumber(vatValue) - toNumber(feeValue);
    setTotalValue(sum.toLocaleString());
  }, [supplyValue, vatValue, feeValue]);

  // ✅ 입금통장 자동완성
  const handleChangeBank = (e) => {
    const value = e.target.value;
    setBankInput(value);
    if (!value.trim()) {
      setFilteredBanks([]);
      setShowBankList(false);
      return;
    }
    const filtered = bankAccounts.filter((acc) => {
      const aliasText = acc.alias
        ? acc.alias
        : `${acc.bank_name}(${acc.account_number?.slice(-6) || ""})`;
      return aliasText.toLowerCase().includes(value.toLowerCase());
    });
    setFilteredBanks(filtered);
    setShowBankList(true);
    setBankHighlightIndex(-1);
  };

  // ✅ 통장 선택 시
  const handlePickBank = (acc) => {
    const displayText = acc.alias
      ? acc.alias.includes("(")
        ? acc.alias
        : `${acc.alias}(${acc.account_number?.slice(-6) || ""})`
      : `${acc.bank_name}(${acc.account_number?.slice(-6) || ""})`;
    setBankInput(displayText);
    setForm((prev) => ({ ...prev, bank_account: acc.id }));
    setShowBankList(false);
  };

  // ✅ 방향키 이동 및 Enter
  const handleBankKeyDown = (e) => {
    if (!showBankList || filteredBanks.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setBankHighlightIndex((prev) =>
        prev < filteredBanks.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setBankHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredBanks.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (bankHighlightIndex >= 0 && filteredBanks[bankHighlightIndex]) {
        handlePickBank(filteredBanks[bankHighlightIndex]);
      }
    }
  };

  return (
    <div className={styles.depositBox}>
      {/* ✅ 1️⃣ 입금 날짜 / 결제방법 / 결제유형 */}
      <div className={styles.rowThreeCol}>
        <div>
          <label>입금된 날짜</label>
          <input
            ref={depositDateRef}
            type="date"
            name="deposit_date"
            value={form.deposit_date || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, deposit_date: e.target.value }))
            }
          />
        </div>

        <div>
          <label>결제방법</label>
          <select
            name="deposit_method"
            value={form.deposit_method || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, deposit_method: e.target.value }))
            }
          >
            <option value="transfer">계좌이체</option>
            <option value="cash">현금결제</option>
            <option value="card">카드결제</option>
            <option value="kakaopay">카카오페이</option>
            <option value="zeropay">제로페이</option>
          </select>
        </div>

        <div>
          <label>결제유형</label>
          <div className={styles.paymentTypeBox}>
            <label>
              <input
                type="checkbox"
                name="tax_type"
                value="과세"
                checked={form.tax_type === "과세"}
                onChange={(e) =>
                  setForm(prev => ({
                    ...prev,
                    tax_type: e.target.checked ? "과세" : ""
                  }))
                }
              /> 과세
            </label>

            <label>
              <input
                type="checkbox"
                name="tax_type"
                value="면세"
                checked={form.tax_type === "면세"}
                onChange={(e) =>
                  setForm(prev => ({
                    ...prev,
                    tax_type: e.target.checked ? "면세" : ""
                  }))
                }
              /> 면세
            </label>

            <label>
              <input
                type="checkbox"
                name="tax_type"
                value="카드과세"
                checked={form.tax_type === "카드과세"}
                onChange={(e) =>
                  setForm(prev => ({
                    ...prev,
                    tax_type: e.target.checked ? "카드과세" : ""
                  }))
                }
              /> 카드과세
            </label>
              
            <label>
              <input
                type="checkbox"
                name="tax_type"
                value="카드면세"
                checked={form.tax_type === "카드면세"}
                onChange={(e) =>
                  setForm(prev => ({
                    ...prev,
                    tax_type: e.target.checked ? "카드면세" : ""
                  }))
                }
              /> 카드면세
            </label>

            {selectedBusiness === "경영팀" && (
              <label>
                <input
                  type="checkbox"
                  name="tax_type"
                  value="영업외수익"
                  checked={form.tax_type === "영업외수익"}
                  onChange={(e) =>
                    setForm(prev => ({
                      ...prev,
                      tax_type: e.target.checked ? "영업외수익" : ""
                    }))
                  }
                /> 영업외수익
              </label>
            )}
          </div>


        </div>
      </div>

      {/* ✅ 2️⃣ 거래처 / 회사명 + 사업자등록번호 */}
      <div className={styles.rowTwoCol}>
        <div style={{ position: "relative" }}>
          <label>거래처 / 회사명</label>
          <input
            type="text"
            value={vendorInput}
            onChange={handleChangeVendor}
            onKeyDown={handleVendorKeyDown}
            placeholder="거래처명 입력 (예: 독산)"
            className={styles.inputField}
          />
          {showVendorList && filteredVendors.length > 0 && (
            <ul className={styles.suggestionList}>
              {filteredVendors.map((v, i) => (
                <li
                  key={v.id}
                  className={i === highlightIndex ? styles.activeItem : ""}
                  onClick={() => handlePickVendor(v)}
                >
                  {v.name}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label>사업자등록번호(자동입력)</label>
          <input
            type="text"
            value={businessNumber || ""}
            readOnly
            placeholder="자동입력"
            className={`${styles.inputField} ${styles.dbField}`}
          />
        </div>
      </div>

      {/* ✅ 3️⃣ 공급가액 / 부가세 / 수수료 / 합계 */}
      <div className={styles.rowFourCol}>
        <div>
          <label>공급가액</label>
          <input
            type="text"
            value={supplyValue}
            onChange={(e) => handleAmountChange(e, setSupplyValue)}
            placeholder="0"
          />
        </div>
        <div>
          <label>부가가치세</label>
          <input
            type="text"
            value={vatValue}
            onChange={(e) => handleAmountChange(e, setVatValue)}
            placeholder="0"
          />
        </div>
        <div>
          <label>수수료</label>
          <input
            type="text"
            value={feeValue}
            onChange={(e) => handleAmountChange(e, setFeeValue)}
            placeholder="0"
          />
        </div>
        <div>
          <label>합계/집행액 (자동계산)</label>
          <input type="text" value={totalValue} readOnly className={styles.dbField} />
        </div>
      </div>

      {/* ✅ 4️⃣ 입금된 통장 + 적요 */}
      <div className={styles.rowBankDesc}>
        <div style={{ position: "relative" }}>
          <label>입금된 통장</label>
          <input
            type="text"
            value={bankInput}
            onChange={handleChangeBank}
            onKeyDown={handleBankKeyDown}
            placeholder="통장명 입력 (예: 환경)"
            className={styles.inputField}
          />
          {showBankList && filteredBanks.length > 0 && (
            <ul className={styles.suggestionList}>
              {filteredBanks.map((acc, i) => {
                const displayText = acc.alias
                  ? acc.alias.includes("(")
                    ? acc.alias
                    : `${acc.alias}(${acc.account_number?.slice(-6) || ""})`
                  : `${acc.bank_name}(${acc.account_number?.slice(-6) || ""})`;
                return (
                  <li
                    key={acc.id}
                    className={i === bankHighlightIndex ? styles.activeItem : ""}
                    onClick={() => handlePickBank(acc)}
                  >
                    {displayText}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <label>적요</label>
          <input
            type="text"
            name="description"
            value={form.description || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="적요 입력"
            className={styles.inputField}
          />
        </div>
      </div>

      {/* ✅ 5️⃣ 비목 / 세목 / 증빙유형 */}
      <div className={styles.rowEvidence}>
        {/* ✅ 비목 */}
        <div style={{ position: "relative" }}>
          <label>비목</label>
          <input
            type="text"
            value={form.bimok || ""}
            onChange={async (e) => {
              const value = e.target.value;
              setForm((prev) => ({ ...prev, bimok: value }));

              if (value.trim().length > 0) {
                try {
                  const res = await api.get("/accounting_accounts/general-sales/search/", {
                    params: { search: value },
                  });
                  const list = Array.isArray(res.data)
                    ? res.data
                    : res.data.results || [];
                  const filtered = list.filter((i) => i.detail);
                  setSuggestions(filtered);
                } catch (err) {
                  console.error("❌ 비목 검색 실패:", err);
                }
              } else {
                setSuggestions([]);
              }
            }}
            placeholder="예: 환경사업 / 방역소득 등"
            className={styles.inputField}
          />

          {suggestions.length > 0 && (
            <ul className={styles.suggestionList}>
              {suggestions.map((item) => {
                const display = [
                  item.category,
                  item.gwang,
                  item.hang,
                  item.sehang,
                  item.mok,
                  item.semok,
                  item.detail,
                ]
                  .filter(Boolean)
                  .join(" / ");
                return (
                  <li
                    key={item.id}
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        bimok: display,
                        account_subject_bimok: item.id,
                      }));
                      setSuggestions([]);
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {display}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ✅ 세목 */}
        <div style={{ position: "relative" }}>
          <label>세목</label>
          <input
            type="text"
            value={form.semok || ""}
            onChange={async (e) => {
              const value = e.target.value;
              setForm((prev) => ({ ...prev, semok: value }));

              if (value.trim().length > 0) {
                try {
                  const res = await api.get("/accounting_accounts/general-sales/search/", {
                    params: { search: value },
                  });
                  const list = Array.isArray(res.data)
                    ? res.data
                    : res.data.results || [];

                  const filtered = list.filter((i) => !i.detail && (i.mok || i.semok));
                  setSuggestions(filtered);
                } catch (err) {
                  console.error("❌ 세목 검색 실패:", err);
                }
              } else {
                setSuggestions([]);
              }
            }}
            placeholder="예: 관리사업 / 행정지원 등"
            className={styles.inputField}
          />

          {suggestions.length > 0 && (
            <ul className={styles.suggestionList}>
              {suggestions.map((item) => {
                const display = [item.mok, item.semok].filter(Boolean).join(" / ");
                return (
                  <li
                    key={item.id}
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        semok: display,
                        account_subject_semok: item.id,
                      }));
                      setSuggestions([]);
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {display}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ✅ 증빙유형 */}
        <div className={styles.evidenceWithSave}>
          <div style={{ flex: 1 }}>
            <label>증빙유형</label>
            <select
              name="evidence_type"
              value={form.evidence_type || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, evidence_type: e.target.value }))
              }
              className={styles.inputField}
            >
              <option value="">선택</option>
              <option value="계산서">계산서</option>
              <option value="세금계산서">세금계산서</option>
              <option value="이체확인증">이체확인증</option>
              <option value="카드매출전표">카드매출전표</option>
            </select>
          </div>

          {/* ✅ 저장버튼 */}
          <div className={styles.saveButtonBox}>
            <button type="button" onClick={handleSubmit} className={styles.saveButton}>
              저장하기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}